import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import { LessonData } from './top-lesson-processor'
import { LessonsMap, StudentProfileWithLessons } from './types'
import { NA } from '../../../constants'
import { getHumanTime } from '../../../services/utils/date-util'
import StatusProgressBar from '../../../components/StatusProgressBar'
import PracticeReportsModal from './PracticeReportsModal'
import "./StudentLessonsSummary.css"

interface Props {
  studentInfo: StudentProfileWithLessons
  selectedTestKind: string
  onResetLessonRequest: (studentEmail: string, studentFullName: string, lessonData: LessonData) => void
  onShowPracticeReportsModal: (selectedStudentEmailAddress: string) => void
}

interface State {
  showDetails: boolean
  showPracticeReportsModal: boolean
  showResetDialogModal: boolean
}

/**
 * Shows the student's categorized lessons summary information.
 */
export class StudentLessonsSummary extends Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      showDetails: false,
      showPracticeReportsModal: false,
      showResetDialogModal: false
    }
  }

  toggleDetails = () => {
    let {showDetails} = this.state
    this.setState({showDetails: !showDetails})
  }

  handleResetLessonRequest = (studentEmail: string, studentFullName: string, lessonData: LessonData) => {
    this.props.onResetLessonRequest(studentEmail, studentFullName, lessonData)
  }

  render() {
    let {studentInfo, selectedTestKind} = this.props
    let {id, emailAddress, firstName, lastName, profileColor, lessons} = studentInfo
    let {showDetails, showPracticeReportsModal} = this.state
    let reportLink = `/print/teacher/student-progress/${emailAddress}`
    let studentFullName = `${firstName} ${lastName}`

    return (
      <div className="student-data-card" id={id} key={`${id}-${emailAddress}`}>
        <div className="student-data-card-header">
          <button className="btn-div" title={`Toggle detailed view for: ${firstName} ${lastName}`} onClick={this.toggleDetails}>
            {/* 😀 */}
            <div className="student-avatar"
              style={{ backgroundColor: profileColor }}
              >
              {firstName[0]+lastName[0]}
            </div>
            <div className="student-basic-info">
              {firstName}
              &nbsp;
              {lastName} - {emailAddress}
            </div>
            <div className="collapsable-btn-container">
              <div className={`collapsable-btn ${showDetails ? "collapsed": ""}`} />
            </div>
          </button>
        </div>

        {showDetails ? 
          <div>
            <div className="progress-summary-report--header">
              <Link className="iconLink--left img-rel progress-summary-report progress-summary-report--link" to={reportLink} target="progress-summary-page"  title={`Open Full Progress Summary for: ${firstName} ${lastName}, opens in a new window`}>
                <img
                  className="top-2"
                  src={`/assets/images/icons/v2/ico-external-link.svg`}
                  width="12"
                  height="12"
                  alt=""
                />
                Open Full Progress Summary
              </Link>
              <button className="btn-simple ml-btn iconLink--left img-rel progress-summary-report--link" title={`View Practice Reports for: ${firstName} ${lastName}, opens in a modal window`} onClick={() => this.props.onShowPracticeReportsModal(emailAddress)}>
                <img
                  className="top-2"
                  src={`/assets/images/icons/v2/ico-modal.svg`}
                  width="12"
                  height="12"
                  alt=""
                />
                View Practice Tests
              </button>
            </div>
            <div className="details" style={{minHeight: 120}} >
              <LessonsDetails 
                lessons={lessons} 
                selectedTestKind={selectedTestKind} 
                onResetLessonRequest={(lessonData) => this.handleResetLessonRequest(emailAddress, studentFullName, lessonData)}/>
            </div>
          </div>
          :
          ""
        }
      </div>
    )
  }
}


function formatTableHeader(first: string, second: string, third: string) {
  return (
    <div>
      <div className="th-tooltip">
        <div>{first}</div>
        <div>{second} </div>
        <span className="th-tooltip-text">{third}</span>
        </div>
    </div>
  )
}

const headers = [
  formatTableHeader('Lesson', '', 'Indicates the name of the lesson'),
  formatTableHeader('Status', '', 'Indicates where a student is within a lesson (e.g., baseline questions, video lessons, or multiple choice questions)'),
  formatTableHeader('Pre-Lesson', 'Accuracy', 'Calculated as the percent of baseline questions answered correctly before starting a lesson'),
  formatTableHeader('Post-Lesson', 'Accuracy', 'Calculated as the number of correct questions divided by the number of questions answered'),
  formatTableHeader('Lesson', 'Growth', 'Calculated as the change in accuracy from pre-lesson accuracy to completed question accuracy'),
  formatTableHeader('Hints on', 'Incorrect', 'Reflects what percent of missed questions students write themselves problem solving hints'),
  formatTableHeader('Time on', 'Questions', 'Indicates average and total time answering multiple choice questions'),
  formatTableHeader('Total', 'Study Time', 'Indicates total time spent answering baseline questions, watching the video lesson, and answering multiple choice questions'),
  formatTableHeader('Reset', 'Lesson', "Reset a student's lesson"),
]

function prepStatusInfo(status, category) {
  return <StatusProgressBar status={status} category={category}/>
}

interface CategoricalLessonsDetailsProps {
  categoryName: string
  categoricalLessons: LessonData[]
  selectedTestKind: string
  onResetLessonRequest: (lessonData: LessonData) => void
}

function prepLessonAccuracyInfo(accuracy: number, testKind: string, scoreInfo="") {
  return (
    <div>
      {accuracy}%
      <div className="act-or-sat-score-info">
        {testKind} <span className="act-or-sat-score">{scoreInfo}</span>
      </div>
    </div>
  )
}

function prepTimeOnQuestionInfo(avgTime: number, totalTime: number) {
  let NA = "N/A"
  let avgTimeInfo = avgTime > 0 ? getHumanTime(avgTime, true, true).text: NA
  let totalTimeInfo = totalTime > 0 ? getHumanTime(totalTime, true, true).text: NA

  return (
    <div className="time-on-question-info act-or-sat-score-info">
      {avgTimeInfo != NA ?
        <div>
          Avg <span dangerouslySetInnerHTML={{__html: avgTimeInfo}}/>
          <br/>
          Total <span dangerouslySetInnerHTML={{__html: totalTimeInfo}}/>
        </div>
        :
        <div className="not-applicable">{NA}</div>
      }
    </div>
  )
}

function prepTotalStudyTimeInfo(totalStudyTime: number) {
  let totalTimeInfo = totalStudyTime > 0 ? getHumanTime(totalStudyTime, true, true).text: NA

  return (
    <div className="time-on-question-info act-or-sat-score-info">
      {totalTimeInfo != NA ?
        <span dangerouslySetInnerHTML={{__html: totalTimeInfo}}/>
        :
        <div className="not-applicable">{NA}</div>
      }
    </div>
  )
}

function getScoreColor(sectionName: string) {
  switch(sectionName) {
      case 'Writing and Language':
      case 'English': {
          return 'purple';
      }
      case 'Reading': {
          return 'orange';
      }
      case 'Science': {
          return 'green';
      }
      case 'Math (No Calculator)':
      case 'Math':
      case 'Math (Calculator)': {
          return 'blue';
      }
      default: {
          return '';
      }
  }
}

function CategoricalLessonsDetails(props: CategoricalLessonsDetailsProps) {
  let {categoryName, categoricalLessons, selectedTestKind} = props
  let categoryClass = categoryName.toLowerCase()
  return (
    <div className="swipeWrapper p-rel">
      <div className={`lesson-list-card-header px-2 ` + getScoreColor(categoryName)}>
        <span className={`ribbon ` + getScoreColor(categoryName)}>
          <img alt="" src={`/assets/images/icons/subjects/ico-subject-${categoryClass}.svg`} width="28" height="27" />
        </span>
        <span>{categoryName}</span>
      </div>
      <div className="fixedHeaderTable">
        <table className={`student-lessons-details-table lesson-table--${categoryClass}`}>
          <thead className={`student-lessons-details-table-head border-bottom border-bottom--${categoryClass}`}>
            <tr>
              {headers.map((headerName, index) =>
                <th className={`label-header ${(index < 2) ? "label-type1": "label-type2"}`} key={`th-${headerName}-${index}`}>{headerName}</th>
              )}
            </tr>
          </thead>
          <tbody className="student-lessons-details-table-body">
              {categoricalLessons && categoricalLessons.map(data => {
                let {status, section: category, avgTimePerQuestion=-1, totalTimeOnEnded=-1, totalTimeOnAnswered=-1} = data
                let {preLessonAccuracy, postLessonAccuracy,} = data
                let {preLessonConversionScoreACT, postLessonConversionScoreACT} = data
                let {preLessonConversionScoreSAT, postLessonConversionScoreSAT} = data
                let preScoreInfo = selectedTestKind === "ACT" ? preLessonConversionScoreACT: preLessonConversionScoreSAT
                let postScoreInfo = selectedTestKind === "ACT" ? postLessonConversionScoreACT: postLessonConversionScoreSAT
                let statusProgress = prepStatusInfo(status, category)
                let timeOnQuestion = prepTimeOnQuestionInfo(avgTimePerQuestion, totalTimeOnAnswered)
                let preLesson = prepLessonAccuracyInfo(preLessonAccuracy, selectedTestKind, preScoreInfo)
                let postLesson = prepLessonAccuracyInfo(postLessonAccuracy, selectedTestKind, postScoreInfo)
                let totalStudyTimeInfo = prepTotalStudyTimeInfo(totalTimeOnEnded)
                let lessonGrowthOutput = data.infoLessonGrowth
                if(lessonGrowthOutput.includes('-')) {
                  lessonGrowthOutput = '<span class="after--down-arrow">' + lessonGrowthOutput + '</span>'
                } else if(lessonGrowthOutput != 'N/A' && lessonGrowthOutput != '0%') {
                  lessonGrowthOutput = '<span class="after--up-arrow">' + lessonGrowthOutput + '</span>'
                } else if (lessonGrowthOutput == '0%' || lessonGrowthOutput === 'N/A') {
                  lessonGrowthOutput = '<span class="not-applicable">N/A</span>'
                }
                return (
                  <tr key={`tr-${data.section}-${data.lessonName}`}>
                    <td className="student-lessons-details-table--col-lesson">{data.lessonName}</td>
                    <td className="student-lessons-details-table--col-status">{statusProgress}</td>
                    <td>
                      {data.infoPreLessonAccuracy == "N/A" ?
                        <span className="not-applicable">
                          N/A
                        </span>
                        :
                        preLesson
                      }
                    </td>
                    <td>
                      {data.infoCompletedQuestionAccuracy == "N/A" ?
                        <span className="not-applicable">
                          N/A
                        </span>
                        :
                        postLesson
                      }
                    </td>
                    <td dangerouslySetInnerHTML={{ __html: lessonGrowthOutput }} />
                    <td>{data.infoHintsOnIncorrect === 'N/A' ?
                      <span className="not-applicable">
                      N/A
                      </span>
                      :
                      data.infoHintsOnIncorrect
                      }
                    </td>
                    <td>{timeOnQuestion}</td>
                    <td>{totalStudyTimeInfo}</td>
                    <td className="student-lesson-reset">
                      <a className="iconLink--right iconLink--only" title={`Reset lesson: ${data.lessonName} for the student`} href="#" onClick={() => props.onResetLessonRequest(data)}>
                        <img
                          className="explanation-button-image"
                          src={`/assets/images/icons/v2/ico-reset.svg`}
                          width="18"
                          height="20"
                          alt=""
                        />
                      </a>
                    </td>
                  </tr>
                )}
              )}
          </tbody>
        </table>
      </div>
      {/*
      <div className="swipeParent">
          <svg version="1.1" className="swipeIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 119.14 122.88"><g><path d="M42.82,67.51c-0.15-0.07-0.33-0.18-0.48-0.3c-1.95-1.55-4.09-3.28-5.94-4.8c-2.69-2.21-5.79-4.76-7.97-6.57 c-1.48-1.22-3.17-2.07-4.76-2.4c-1.03-0.18-1.96-0.18-2.69,0.11c-0.59,0.26-1.11,0.74-1.44,1.48c-0.44,1-0.66,2.4-0.55,4.32 c0.11,1.7,0.7,3.54,1.48,5.35c1.14,2.62,2.73,5.05,3.91,6.6c0.07,0.11,0.15,0.18,0.18,0.3l23.35,33.35c0.3,0.44,0.48,0.92,0.52,1.4 c0.48,3.84,1.29,6.75,2.47,8.56c0.88,1.33,1.99,1.99,3.43,1.95h36.74c2.29-0.04,4.35-0.7,6.27-2.03c2.1-1.44,3.98-3.69,5.72-6.71 c0.04-0.04,0.07-0.11,0.11-0.15c0.66-1.14,1.55-2.62,2.4-4.02c3.73-6.12,6.97-11.47,7.34-19.07l-0.22-10.48 c-0.04-0.15-0.04-0.29-0.04-0.44c0-0.15,0-1.14,0.04-2.47c0.07-6.93,0.18-15.49-6.16-16.56h-4.09c-0.04,1.95-0.15,3.95-0.26,5.87 c-0.11,1.73-0.22,3.36-0.22,4.94c0,1.7-1.36,3.06-3.06,3.06c-1.7,0-3.06-1.36-3.06-3.06c0-1.59,0.11-3.43,0.22-5.35 c0.41-6.53,0.89-14.02-4.32-14.94h-4.06c-0.22,0-0.44-0.04-0.66-0.07c0.04,2.36-0.11,4.8-0.26,7.16c-0.11,1.73-0.22,3.36-0.22,4.94 c0,1.7-1.37,3.06-3.06,3.06c-1.7,0-3.06-1.36-3.06-3.06c0-1.59,0.11-3.43,0.22-5.35c0.41-6.53,0.88-14.02-4.32-14.94h-4.06 c-0.3,0-0.55-0.04-0.81-0.11v11.92c0,1.7-1.36,3.06-3.06,3.06c-1.7,0-3.06-1.37-3.06-3.06V17.01c0-5.35-2.18-8.74-4.98-10.14 c-1.03-0.52-2.14-0.77-3.21-0.77c-1.07,0-2.18,0.26-3.21,0.77c-2.77,1.4-4.91,4.8-4.91,10.29v56.03c0,1.7-1.36,3.06-3.06,3.06 c-1.7,0-3.06-1.37-3.06-3.06v-5.68H42.82L42.82,67.51z M21.16,30.41c-0.99,1.04-2.64,1.08-3.69,0.09 c-1.04-0.99-1.08-2.64-0.09-3.69l7.37-7.71H2.61C1.17,19.11,0,17.94,0,16.49c0-1.44,1.17-2.61,2.61-2.61h22.14l-7.38-7.75 c-0.99-1.04-0.95-2.69,0.09-3.69c1.04-0.99,2.69-0.95,3.69,0.09l11.58,12.16c0.95,1,0.96,2.58,0,3.6L21.16,30.41L21.16,30.41z M71.44,31.13c0.26-0.07,0.52-0.11,0.81-0.11h4.24c0.22,0,0.48,0.04,0.7,0.07c5.64,0.89,8.19,4.17,9.22,8.45 c0.41-0.18,0.85-0.3,1.29-0.3h4.24c0.22,0,0.48,0.04,0.7,0.07c6.09,0.96,8.52,4.68,9.41,9.41c0.15-0.04,0.3-0.04,0.48-0.04h4.24 c0.22,0,0.48,0.04,0.7,0.07c11.66,1.81,11.51,13.39,11.4,22.72v2.43l0.26,10.77v0.33c-0.44,9.19-4.06,15.12-8.23,21.99 c-0.7,1.14-1.4,2.32-2.36,3.95c-0.04,0.04-0.04,0.07-0.07,0.11c-2.18,3.8-4.68,6.71-7.56,8.71c-2.91,2.03-6.16,3.06-9.7,3.1h-36.7 c-3.65,0.07-6.49-1.51-8.6-4.65c-1.7-2.51-2.8-6.05-3.39-10.48L19.65,75.07l-0.11-0.11c-1.36-1.81-3.21-4.65-4.61-7.78 c-1.03-2.36-1.81-4.91-1.99-7.41c-0.18-2.99,0.22-5.35,1.07-7.23c1.03-2.32,2.73-3.84,4.76-4.65c1.88-0.77,4.02-0.89,6.16-0.44 c2.58,0.52,5.24,1.81,7.49,3.69c1.84,1.55,4.94,4.06,7.97,6.53l2.51,2.07V17.19c0-8.15,3.62-13.39,8.3-15.75 C53.07,0.48,55.1,0,57.17,0c2.07,0,4.09,0.48,5.98,1.44c4.69,2.36,8.37,7.64,8.37,15.64v14.09L71.44,31.13L71.44,31.13z"/></g></svg>
        </div>
      */}
    </div>
  )
}

interface LessonsDetailsProps {
  lessons: LessonsMap
  selectedTestKind: string
  onResetLessonRequest: (lessonData: LessonData) => void
}

function LessonsDetails(props: LessonsDetailsProps) {
  let {lessons, selectedTestKind} = props
  let categories = ["English", "Math", "Reading", "Science"]
  let nofCategoriesStarted = categories.reduce((acc, cur)=> {
    let categoricalLessons = lessons[cur]
    if (categoricalLessons) acc++
    return acc
  }, 0)
  
  return (
    <div>
      {nofCategoriesStarted > 0 ?
        categories.map(categoryName => {
          let categoricalLessons = lessons[categoryName]
          return (
            <div className="student-lessons-details" key={categoryName}>
              {categoricalLessons && 
                <CategoricalLessonsDetails 
                  categoryName={categoryName} 
                  categoricalLessons={categoricalLessons} 
                  selectedTestKind={selectedTestKind}
                  onResetLessonRequest={props.onResetLessonRequest}
                  />
                }
            </div>
          )
        })
        :
        <div className="no-lessons-found">
          No lessons found!
        </div>
      }
    </div>
  )
}
